import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useNotification,
  useTheme,
} from '@prio365/prio365-react-library';
import { Progress } from 'antd';
import React, { useCallback, useRef, useState } from 'react';
import { DropTargetMonitor } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getProject,
  RootReducerState,
} from '../../../../../apps/main/rootReducer';
import Flex from '../../../../../components/Flex';
import DroppableElement from '../../../../../dnd/components/DroppableElement';
import {
  DND_TYPE_DRIVE_ITEM_FILE,
  DND_TYPE_EMAIL,
  DND_TYPE_EMAIL_ATTACHMENT,
  DndDriveItemDto,
  DndEmailAttachmentDto,
  DndEmailDto,
} from '../../../../../dnd/types';
import { DriveItem } from '../../../../../models/Drive';
import { PrioShareFileDto } from '../../../../../models/PrioShare';
import { Project } from '../../../../../models/Project';
import { ProjectId } from '../../../../../models/Types';
import { PrioTheme } from '../../../../../theme/types';
import { makePrioStyles } from '../../../../../theme/utils';
import { apiGetBlobOfDriveItem } from '../../../../documents/api';
import {
  apiDownloadAttachmentToCache,
  apiDownloadEmlAsBlob,
} from '../../../../mail/api';
import { usePrioShareDraft } from '../PrioShareDraftContext';
import PrioShareSelectableFileList from './PrioShareSelectableFileList';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  uploadField: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.extraSmall,
    cursor: 'pointer',
    border: `2px dashed ${theme.colors.application.border}`,
    borderRadius: theme.borderRadius.regular,
    padding: theme.spacing.small,
    minHeight: 56,
    boxSizing: 'border-box' as const,
    color: theme.colors.application.typography.active,
    transition: 'background 0.15s ease, border-color 0.15s ease',

    '&:hover': {
      background: theme.colors.base.blue[20],
      border: `2px solid ${theme.colors.application.border}`,
    },
  },
  uploadProgress: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing.extraSmall,
    width: '100%',
  },
  uploadProgressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: theme.font.fontSize.extraSmall,
    color: theme.colors.application.typography.active,
  },
  uploadProgressFilename: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    flex: 1,
    marginRight: theme.spacing.small,
  },
  uploadProgressPercent: {
    flexShrink: 0,
    fontWeight: 600,
  },
  uploadProgressPreparing: {
    fontSize: theme.font.fontSize.tiny,
    color: theme.colors.application.typography.muted,
    textAlign: 'center' as const,
  },
}));

interface PrioShareFileUploadZoneProps {
  uploadedFiles: PrioShareFileDto[];
  onFileAdded: (file: PrioShareFileDto) => void;
  onFileRemoved: (fileId: string) => void;
}

const PrioShareFileUploadZone: React.FC<PrioShareFileUploadZoneProps> = ({
  uploadedFiles,
  onFileAdded,
  onFileRemoved,
}) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const [notification, notificationContextHolder] = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { projectId, projectExtensionId, draft, uploadFile, isUploading, uploadProgress, isDraftReady } =
    usePrioShareDraft();

  const project = useSelector<RootReducerState, Project>((state) =>
    getProject(state, projectId)
  );
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropLoading, setDropLoading] = useState(false);
  const [currentFileName, setCurrentFileName] = useState<string>('');

  const isDisabled = isUploading || dropLoading || !isDraftReady;
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files;
      try {
        if (selectedFiles && selectedFiles.length > 0) {
          for (const file of Array.from(selectedFiles)) {
            setCurrentFileName(file.name);
            const uploaded = await uploadFile(file, file.name);
            if (uploaded) {
              onFileAdded(uploaded);
            } else {
              notification.error({
                title: t(
                  'common:widgetArea.prioShareWidget.createForm.uploadError'
                ),
              });
            }
          }
        }
      } catch {
        notification.error({
          title: t(
            'common:widgetArea.prioShareWidget.createForm.uploadError'
          ),
        });
      } finally {
        setCurrentFileName('');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [uploadFile, onFileAdded, notification, t]
  );

  const handleUploadZoneClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      if (isUploading) return;
      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles && droppedFiles.length > 0) {
        try {
          for (const file of Array.from(droppedFiles)) {
            setCurrentFileName(file.name);
            const uploaded = await uploadFile(file, file.name);
            if (uploaded) {
              onFileAdded(uploaded);
            } else {
              notification.error({
                title: t(
                  'common:widgetArea.prioShareWidget.createForm.uploadError'
                ),
              });
            }
          }
        } finally {
          setCurrentFileName('');
        }
      }
    },
    [uploadFile, isUploading, onFileAdded, notification, t]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const disableDndDrag = useCallback(
    (
      _item: DndDriveItemDto | DndEmailAttachmentDto | DndEmailDto,
      monitor: DropTargetMonitor
    ) => {
      if (monitor.getItemType() === DND_TYPE_EMAIL_ATTACHMENT) {
        return false;
      }
      if (monitor.getItemType() === DND_TYPE_EMAIL) {
        return false;
      }
      if (monitor.getItemType() === DND_TYPE_DRIVE_ITEM_FILE) {
        const draggedElement = monitor.getItem() as DndDriveItemDto;
        return draggedElement.driveItems?.some(
          (driveItem) => !!driveItem.folder
        );
      }
      return true;
    },
    []
  );

  const handleDndDrop = useCallback(
    async (
      item: DndDriveItemDto | DndEmailAttachmentDto | DndEmailDto,
      monitor: DropTargetMonitor
    ) => {
      setDropLoading(true);
      try {
        if (monitor.getItemType() === DND_TYPE_EMAIL) {
          const emailItem = item as DndEmailDto & { projectId: ProjectId };
          const messages = emailItem.selectedMessages?.length
            ? emailItem.selectedMessages
            : [emailItem.message];

          const sourceProjectId = emailItem.projectId ?? projectId;

          for (const message of messages) {
            const blob = await apiDownloadEmlAsBlob(
              sourceProjectId,
              message.id
            );
            if (!blob) {
              notification.error({
                title: t(
                  'common:widgetArea.prioShareWidget.createForm.uploadError'
                ),
              });
              continue;
            }
            const fileName = `${message.subject || 'email'}.eml`;
            const file = new File([blob], fileName, {
              type: 'message/rfc822',
            });
            setCurrentFileName(file.name);
            const uploaded = await uploadFile(file, file.name);
            if (uploaded) {
              onFileAdded(uploaded);
            } else {
              notification.error({
                title: t(
                  'common:widgetArea.prioShareWidget.createForm.uploadError'
                ),
              });
            }
          }
          setCurrentFileName('');
          return;
        }

        if (monitor.getItemType() === DND_TYPE_EMAIL_ATTACHMENT) {
          const attachmentItem = item as DndEmailAttachmentDto;
          const attachments = attachmentItem.selectedAttachments?.length
            ? attachmentItem.selectedAttachments
            : attachmentItem.draggableMessageAttachment
              ? [attachmentItem.draggableMessageAttachment.attachment]
              : [];

          const messageId =
            attachmentItem.draggableMessageAttachment?.messageId;

          for (const attachment of attachments) {
            const blob = await apiDownloadAttachmentToCache(
              projectId,
              attachment.messageId ?? messageId,
              attachment
            );
            if (!blob) {
              notification.error({
                title: t(
                  'common:widgetArea.prioShareWidget.createForm.uploadError'
                ),
              });
              continue;
            }
            const file = new File([blob], attachment.name, {
              type: attachment.contentType,
            });
            setCurrentFileName(file.name);
            const uploaded = await uploadFile(file, file.name);
            if (uploaded) {
              onFileAdded(uploaded);
            } else {
              notification.error({
                title: t(
                  'common:widgetArea.prioShareWidget.createForm.uploadError'
                ),
              });
            }
          }
          setCurrentFileName('');
          return;
        }

        if (monitor.getItemType() === DND_TYPE_DRIVE_ITEM_FILE) {
          if (!project?.groupId) return;
          const draggedElement = item as DndDriveItemDto;
          const driveItems = draggedElement.driveItems ?? [
            draggedElement.driveItem,
          ];
          for (const driveItem of driveItems) {
            if (driveItem.folder) continue;
            const blob = await apiGetBlobOfDriveItem(
              project.groupId,
              driveItem as DriveItem
            );
            if (!blob) continue;
            const file = new File([blob], driveItem.name, {
              type: (driveItem as DriveItem).file?.mimeType,
            });
            setCurrentFileName(file.name);
            const uploaded = await uploadFile(file, file.name);
            if (uploaded) {
              onFileAdded(uploaded);
            } else {
              notification.error({
                title: t(
                  'common:widgetArea.prioShareWidget.createForm.uploadError'
                ),
              });
            }
          }
          setCurrentFileName('');
        }
      } finally {
        setDropLoading(false);
      }
    },
    [project?.groupId, projectId, uploadFile, onFileAdded, notification, t]
  );
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  return (
    <Flex.Column childrenGap={6}>
      {notificationContextHolder}
      <PrioShareSelectableFileList
        files={uploadedFiles}
        projectExtensionId={projectExtensionId}
        prioShareId={draft?.prioShareId ?? ''}
        onRemove={onFileRemoved}
      />
      <DroppableElement
        accept={[DND_TYPE_DRIVE_ITEM_FILE, DND_TYPE_EMAIL_ATTACHMENT, DND_TYPE_EMAIL]}
        onDrop={handleDndDrop}
        disable={disableDndDrag}
        object={{ projectId }}
        activeOverlay
      >
        <div
          className={classes.uploadField}
          onClick={isDisabled ? undefined : handleUploadZoneClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          style={
            isDisabled
              ? { opacity: 0.5, cursor: 'not-allowed' }
              : isDragOver
                ? {
                    background: theme.colors.base.blue[20],
                    border: `2px solid ${theme.colors.application.border}`,
                  }
                : undefined
          }
        >
          {dropLoading || isUploading ? (
            <div className={classes.uploadProgress}>
              <div className={classes.uploadProgressLabel}>
                <span className={classes.uploadProgressFilename}>
                  {currentFileName ||
                    t('common:widgetArea.prioShareWidget.createForm.uploadText')}
                </span>
                {uploadProgress > 0 && (
                  <span className={classes.uploadProgressPercent}>
                    {uploadProgress}%
                  </span>
                )}
              </div>
              {uploadProgress > 0 ? (
                <Progress
                  percent={uploadProgress}
                  status={uploadProgress === 100 ? 'success' : 'active'}
                  showInfo={false}
                  strokeWidth={4}
                  strokeColor={theme.colors.base.blue[80] ?? '#1890ff'}
                />
              ) : (
                <span className={classes.uploadProgressPreparing}>
                  {t(
                    'common:widgetArea.prioShareWidget.createForm.uploadPreparing'
                  )}
                </span>
              )}
            </div>
          ) : (
            <>
              <FontAwesomeIcon
                icon={['fal', 'upload']}
                style={{ fontSize: theme.font.fontSize.regular }}
              />
              <span style={{ fontSize: theme.font.fontSize.extraSmall }}>
                {t(
                  'common:widgetArea.prioShareWidget.createForm.uploadText'
                )}
              </span>
            </>
          )}
        </div>
      </DroppableElement>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
    </Flex.Column>
  );
};

export default PrioShareFileUploadZone;
